import React, { forwardRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Tooltip, { type TooltipPrimitiveProps } from '@atlaskit/tooltip';

const messages = defineMessages({
	tenureTooltipHeading: {
		id: 'user-profile-preview.tenure-bar.tenure-tooltip-heading',
		defaultMessage: '{percent}% ({count})',
		description: 'Heading in the tenure bar tooltip showing percentage and count of employees',
	},
	tenureTooltipBefore: {
		id: 'user-profile-preview.tenure-bar.tenure-tooltip-before',
		defaultMessage:
			'{count, plural, =0 {No one was hired before {name}} one {# person was hired before {name}} other {# people were hired before {name}}}',
		description: 'Tooltip content for the tenure bar section showing people hired before the user',
	},
	tenureTooltipAfter: {
		id: 'user-profile-preview.tenure-bar.tenure-tooltip-after',
		defaultMessage:
			'{count, plural, =0 {No one was hired after {name}} one {# person was hired after {name}} other {# people were hired after {name}}}',
		description: 'Tooltip content for the tenure bar section showing people hired after the user',
	},
});

type TenureBarTense = 'before' | 'after';

const styles = cssMap({
	wrapper: {
		position: 'relative',
		width: '100%',
		height: '12px',
		display: 'flex',
		alignItems: 'center',
	},
	bar: {
		display: 'flex',
		flexWrap: 'nowrap',
		width: '100%',
		position: 'relative',
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
	sectionWrapper: {
		height: '8px',
		transitionProperty: 'opacity',
		transitionDuration: '200ms',
		transitionTimingFunction: 'ease-in-out',
		position: 'relative',
	},
	sectionTarget: {
		height: '8px',
		width: '100%',
		cursor: 'pointer',
		display: 'block',
	},
	separator: {
		position: 'absolute',
		height: '12px',
		transform: 'translate(-50%, -50%)',
		zIndex: 1,
		top: token('space.075'),
	},
	tooltipWrapper: {
		position: 'fixed',
	},
	tooltip: {
		boxSizing: 'border-box',
		borderRadius: token('radius.small'),
		insetBlockStart: token('space.0'),
		insetInlineStart: token('space.0'),
		overflowWrap: 'break-word',
		paddingInline: token('space.150'),
		paddingBlock: token('space.150'),
		wordWrap: 'break-word',
		backgroundColor: token('elevation.surface.overlay'),
		boxShadow: token('elevation.shadow.overlay'),
	},
});

const OverlayTooltip = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function OverlayTooltip(
	{ style, children, placement, onMouseOut, onMouseOver },
	ref,
) {
	return (
		<Box
			ref={ref}
			xcss={styles.tooltipWrapper}
			style={{
				top: style?.top,
				right: style?.right,
				bottom: style?.bottom,
				left: style?.left,
				transform: style?.transform,
			}}
			onMouseOut={onMouseOut}
			onMouseOver={onMouseOver}
		>
			<Stack xcss={styles.tooltip} data-placement={placement}>
				{children}
			</Stack>
		</Box>
	);
});

const MIN_WIDTH = 10;

function clampWidth(percent: number): number {
	if (percent < MIN_WIDTH) {
		return MIN_WIDTH;
	}
	if (percent > 100 - MIN_WIDTH) {
		return 100 - MIN_WIDTH;
	}
	return percent;
}

interface TenureBarSectionProps {
	tense: TenureBarTense;
	color: string;
	percent: number;
	count: number;
	name: string;
	hoveredSection: TenureBarTense | null;
	onHover: (tense: TenureBarTense | null) => void;
}

function TenureBarSection({
	tense,
	color,
	percent,
	count,
	name,
	hoveredSection,
	onHover,
}: TenureBarSectionProps) {
	const intl = useIntl();
	const visualWidth = clampWidth(percent);
	const isFaded = hoveredSection !== null && hoveredSection !== tense;

	const tooltipMessage =
		tense === 'before'
			? intl.formatMessage(messages.tenureTooltipBefore, { count, name })
			: intl.formatMessage(messages.tenureTooltipAfter, { count, name });

	return (
		<Box
			xcss={styles.sectionWrapper}
			style={{
				width: `${visualWidth}%`,
				backgroundColor: color,
				opacity: isFaded ? 0.5 : 1,
			}}
			onMouseEnter={() => onHover(tense)}
			onMouseLeave={() => onHover(null)}
		>
			<Tooltip
				content={
					<Stack>
						<Heading size="small">
							{intl.formatMessage(messages.tenureTooltipHeading, {
								percent,
								count: intl.formatNumber(count),
							})}
						</Heading>
						<Text color="color.text.subtle" size="small">
							{tooltipMessage}
						</Text>
					</Stack>
				}
				component={OverlayTooltip}
				position="bottom-start"
			>
				<Box xcss={styles.sectionTarget} tabIndex={0} testId={`tenure-bar-section-${tense}`} />
			</Tooltip>
		</Box>
	);
}

interface TenureBarProps {
	beforePercent: number;
	beforeCount?: number;
	afterCount?: number;
	userName?: string;
	testId?: string;
}

export function TenureBar({
	beforePercent,
	beforeCount = 0,
	afterCount = 0,
	userName = '',
	testId = 'tenure-bar',
}: TenureBarProps) {
	const [hoveredSection, setHoveredSection] = useState<TenureBarTense | null>(null);
	const visualWidth = clampWidth(beforePercent);
	const afterPercent = 100 - beforePercent;

	return (
		<Box xcss={styles.wrapper} testId={testId}>
			<Box xcss={styles.bar}>
				<TenureBarSection
					tense="before"
					percent={beforePercent}
					count={beforeCount}
					name={userName}
					hoveredSection={hoveredSection}
					onHover={setHoveredSection}
					color={
						hoveredSection === 'before'
							? token('color.chart.success.hovered')
							: token('color.chart.success')
					}
				/>
				<TenureBarSection
					tense="after"
					percent={afterPercent}
					count={afterCount}
					name={userName}
					hoveredSection={hoveredSection}
					onHover={setHoveredSection}
					color={
						hoveredSection === 'after'
							? token('color.chart.information.hovered')
							: token('color.chart.information')
					}
				/>
			</Box>
			<Box
				xcss={styles.separator}
				style={{
					left: `${visualWidth}%`,
					backgroundColor: hoveredSection
						? token('color.border.accent.gray')
						: token('elevation.surface'),
					width: hoveredSection ? '2px' : '1px',
				}}
			/>
		</Box>
	);
}
